import { useMemo } from "react";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  isSessionSuperAdmin,
  getSessionRoleName,
} from "../../../utils/permissionUtils";
import { organizationHasEPlannerModule } from "../../../utils/orgModuleAccess";
import { PERMISSIONS } from "../../../constants/permissions";

export { organizationHasEPlannerModule };

/** Assignable E-Planner keys (Role / User permission cards). */
export const EPLANNER_PERMISSION_KEYS = {
  BOOK: "BOOK_APPOINTMENT",
  CANCEL: "CANCEL_APPOINTMENT",
  RESCHEDULE: "RESCHEDULE_APPOINTMENT",
  CHANGE_STATUS: "CHANGE_APPOINTMENT_STATUS",
  VIEW_LIST: "VIEW_APPOINTMENT_LIST",
  VIEW_CALENDAR: "VIEW_APPOINTMENT_CALENDAR",
  VIEW_OCCUPANCY: "VIEW_LOCATION_OCCUPANCY",
  VIEW_CHANGES: "VIEW_APPOINTMENT_CHANGES",
  VIEW_ANALYTICS: "VIEW_EPLANNER_ANALYTICS",
  VIEW_COMMUNICATION: "VIEW_WHATSAPP_COMMUNICATION",
  /** Restrictive: when assigned (non–System Admin), hide names and show Vol ID only. */
  VIEW_PARTICIPANT_ID_ONLY: "VIEW_PARTICIPANT_ID_ONLY",
};

export function canAccessEplanner({ userData, isSystemAdmin }) {
  return isSystemAdmin || organizationHasEPlannerModule(userData);
}

function hasEplannerKey(permissions, key) {
  return hasPermission(permissions, key);
}

/**
 * Shared CAPA-style access for E-Planner.
 * System Administrator / Super Admin → all keys (via getAllPermissionKeys).
 */
export function useEplannerAccess() {
  const { userData, loading } = useUserData();
  const sessionRole = getSessionRoleName(userData);
  const isSystemAdmin =
    isSystemAdministratorRole(sessionRole) || isSessionSuperAdmin(userData);

  const permissions = useMemo(
    () =>
      isSystemAdmin
        ? getAllPermissionKeys()
        : parsePermissions(userData?.Permission || ""),
    [isSystemAdmin, userData?.Permission],
  );

  const moduleAccess = canAccessEplanner({ userData, isSystemAdmin });

  const can = (key) => {
    if (isSystemAdmin) return true;
    if (!moduleAccess) return false;
    return hasEplannerKey(permissions, key);
  };

  const canBook = can(EPLANNER_PERMISSION_KEYS.BOOK);
  const canCancel = can(EPLANNER_PERMISSION_KEYS.CANCEL);
  const canReschedule = can(EPLANNER_PERMISSION_KEYS.RESCHEDULE);
  const canChangeStatus = can(EPLANNER_PERMISSION_KEYS.CHANGE_STATUS);
  const canViewList = can(EPLANNER_PERMISSION_KEYS.VIEW_LIST);
  const canViewCalendar = can(EPLANNER_PERMISSION_KEYS.VIEW_CALENDAR);
  const canViewOccupancy = can(EPLANNER_PERMISSION_KEYS.VIEW_OCCUPANCY);
  const canViewChanges = can(EPLANNER_PERMISSION_KEYS.VIEW_CHANGES);
  const canViewAnalytics = can(EPLANNER_PERMISSION_KEYS.VIEW_ANALYTICS);
  const canViewCommunication = can(EPLANNER_PERMISSION_KEYS.VIEW_COMMUNICATION);

  /**
   * Restrictive privacy flag — System Administrator / Super Admin always see names.
   * Non-admins with VIEW_PARTICIPANT_ID_ONLY see Vol ID badges only (UI hide).
   */
  const hideParticipantName =
    !isSystemAdmin &&
    hasEplannerKey(permissions, EPLANNER_PERMISSION_KEYS.VIEW_PARTICIPANT_ID_ONLY);

  /** Any manage-style action (notes / screening edits that sit with manage UI). */
  const canManage = canCancel || canReschedule || canChangeStatus;

  /** Any E-Planner screen or action — used for coarse module entry. */
  const canView =
    canViewList ||
    canViewCalendar ||
    canViewOccupancy ||
    canViewChanges ||
    canViewAnalytics ||
    canViewCommunication ||
    canBook ||
    canManage;

  return {
    userData,
    loading,
    permissions,
    isSystemAdmin,
    canAccess: moduleAccess,
    canView,
    canBook,
    canCancel,
    canReschedule,
    canChangeStatus,
    canViewList,
    canViewCalendar,
    canViewOccupancy,
    canViewChanges,
    canViewAnalytics,
    canViewCommunication,
    hideParticipantName,
    canViewParticipantIdOnly: hideParticipantName,
    canManage,
    eplannerPermissions: PERMISSIONS.EPLANNER,
  };
}
